import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom"; // to get activityUrl from route
import "./DedActivityList.css";
import { Activity, getDataForDedActivityListPageApi } from "../../lib/api";
import DetPagination from "./DetPagination";

/* --------- Main Page --------- */
const DedActivityList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activityUrl } = useParams<{ activityUrl: string }>();
  const [category, setCategory] = useState<Activity | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const activityGroup = searchParams.get("activityGroup") || null; // Contracting and building works

  const handleGetActivityDetails = (activityCode: string) => {
    navigate(`/dubai-economy/activities/${activityUrl}/${activityCode}`);
  };
  // Fetch category & subActivities
  useEffect(() => {
    if (!activityUrl) return;
    const fetchActivities = async () => {
      setLoading(true);
      try {
        const data: Activity = await getDataForDedActivityListPageApi(
          activityUrl,
          activityGroup
        );
        setCategory(data);
      } catch (err) {
        console.error("Failed to fetch activities:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [activityUrl]);

  const filtered = useMemo(() => {
    if (!category) return [];
    const q = query.trim().toLowerCase();
    if (!q) return category.subActivities;
    return category.subActivities.filter(
      (r) =>
        r.activityCode.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.groupName.toLowerCase().includes(q) ||
        r.licenseType.toLowerCase().includes(q)
    );
  }, [query, category]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const start = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  if (loading) return <div className="text-center py-5">Loading...</div>;
  if (!category)
    return (
      <div className="text-center py-5 text-muted">No category found.</div>
    );

  return (
    <div className="container py-4 py-md-5">
      {/* Search */}
      <div className="ded-activity-search-section">
        <div className="ded-activity-search-wrap">
          <input
            className="form-control ded-activity-search"
            placeholder="Search activity or sub activity"
            value={query}
            onChange={(e) => {
              setPage(1);
              setQuery(e.target.value);
            }}
          />
          <button
            className="ded-activity-search-btn"
            type="button"
            aria-label="Search"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="M21 21L16.65 16.65"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Heading + count */}
      <div className="ded-activity-topline">
        <div className="ded-activity-title">{category.name}</div>
        <div className="ded-activity-result-count">
          You have {filtered.length.toLocaleString()} results
        </div>
      </div>

      {/* Table */}
      <div className="ded-activity-table-wrap">
        <div className="table-responsive mb-0">
          <table className="table mb-0 ded-activity-table">
            <thead>
              <tr>
                <th style={{ width: "140px" }}>Activity Code</th>
                <th>Activity</th>
                <th>Activity Group</th>
                <th style={{ width: "180px" }} className="text-end">
                  Licence Type
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((item) => (
                <tr
                  key={`detail-${item.categoryID}-${item.activityCode}`}
                  className="ded-activity-row"
                >
                  <td
                    className="ded-activity-code"
                    onClick={() => handleGetActivityDetails(item.activityCode)}
                  >
                    <a
                      href="javascript:void(0);"
                      aria-label={`Open ${item.activityCode}`}
                    >
                      {item.activityCode}
                    </a>
                  </td>
                  <td>{item.name}</td>
                  <td>{item.groupName}</td>
                  <td className="text-end">
                    <span className="ded-activity-licence-pill">
                      {item.licenseType}
                    </span>
                  </td>
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-muted">
                    No results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <DetPagination page={safePage} pages={pages} onChange={setPage} />
      </div>
    </div>
  );
};

export default DedActivityList;
